import React, { useEffect, Dispatch, useState, useCallback } from "react";
import { Dropdown, IDropdownOption, MessageBar, MessageBarType } from "@fluentui/react";
import { wizardServices } from "cayo.ui.wizards";
import { messages } from "../messages";
import styled from "styled-components";
import { useIntl, FormattedMessage } from "react-intl";
import { useTheme, LoadingOverlay } from "cayo.ui";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import { getAppSubscriptions } from "../api";

interface ISpecifyAccount {
  dispatchWizardState: Dispatch<{ type: string; payload: any }>;
  wizardState: any;
}

export const SelectSubscription = ({ wizardState, dispatchWizardState }: ISpecifyAccount) => {
  const azureSubscriptionService = wizardServices.azureSubscriptionService;
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>();
  const { cayoTheme } = useTheme();
  const [subscriptionsDropDownOptions, setSubscriptionsDropDownOptions] = useState<
    IDropdownOption[]
  >([]);

  const selectSubscription = useCallback((s: IDropdownOption) => {
    dispatchWizardState({ type: "update", payload: { subscription: s.data } });
    setSelectedKey(s?.key as string);
  }, []);

  useEffect(() => {
    if (!wizardState.refreshToken) {
      return;
    }

    setIsLoading(true);

    const loadSubscriptions = async () => {
      try {
        const subscriptions = await azureSubscriptionService.getSubscriptions(
          wizardState.refreshToken!
        );

        subscriptions.forEach((s) => (s.subscriptionId = s.id));
        dispatchWizardState({ type: "update", payload: { subscriptions } });

        const options =
          subscriptions?.map<IDropdownOption>((s) => ({
            key: s.id!,
            text: (s as any).displayName!,
            data: s,
          })) || [];

        if (options?.length > 0) {
          selectSubscription(options[0]);
          setSubscriptionsDropDownOptions(options);
        }
      } catch (e) {
        const error = await ajaxUtils.getError(e);
        dispatchWizardState({
          type: "update",
          payload: { subscriptionsError: error?.error_description || error?.error },
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscriptions();
  }, [wizardState.refreshToken]);

  useEffect(() => {
    if (!wizardState.subscriptionLoadTrigger || !wizardState.certificate) {
      return;
    }

    dispatchWizardState({ type: "update", payload: { subscriptionsError: "" } });
    setIsLoading(true);
    setSubscriptionsDropDownOptions([]);

    const loadSubscriptions = async () => {
      try {
        const payload = {
          appId: wizardState.applicationId,
          tenantId: wizardState.tenantId,
          certificate: wizardState.certificate,
          ...(wizardState.certificatePassword
            ? { certificatePassword: wizardState.certificatePassword }
            : {}),
        };
        const subscriptions = await getAppSubscriptions(payload);

        subscriptions.forEach((s) => (s.subscriptionId = s.id));
        dispatchWizardState({ type: "update", payload: { subscriptions } });

        const options =
          subscriptions?.map<IDropdownOption>((s) => ({
            key: s.id!,
            text: (s as any).displayName!,
            data: s,
          })) || [];

        if (options?.length > 0) {
          selectSubscription(options[0]);
          setSubscriptionsDropDownOptions(options);
        }
      } catch (e) {
        const error = await ajaxUtils.getError(e);
        dispatchWizardState({
          type: "update",
          payload: { subscriptionsError: error?.error_description || error?.error },
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscriptions();
  }, [wizardState.subscriptionLoadTrigger]);

  return (
    <Body>
      {isLoading && <LoadingOverlay text={intl.formatMessage(messages.loadingSubscriptions)} />}

      {wizardState.subscriptionsError && (
        <MessageBar
          messageBarType={MessageBarType.error}
          truncated={false}
          isMultiline
          styles={{ root: { marginBottom: 10 } }}
        >
          {wizardState.subscriptionsError}
        </MessageBar>
      )}

      <MessageBar
        messageBarType={MessageBarType.info}
        truncated={true}
        styles={{
          root: {
            margin: "10px 0 10px 0",
            background: cayoTheme.wizard.colors.active,
          },
        }}
      >
        {intl.formatMessage(messages.selectAzureResourceInfo, {
          link: (
            <a
              href={"https://support.cayosoft.com/hc/en-us/articles/42065276454541"}
              target="_blank"
              rel="noreferrer"
            >
              <FormattedMessage
                id={messages.learnMore.id}
                defaultMessage={messages.learnMore.defaultMessage}
              />
            </a>
          ),
        })}
      </MessageBar>

      <Dropdown
        label={intl.formatMessage(messages.selectAzureResourceField)}
        options={subscriptionsDropDownOptions}
        styles={{ root: { width: "100%" } }}
        required
        disabled={!subscriptionsDropDownOptions?.length}
        selectedKey={selectedKey}
        onChange={(e, s) => selectSubscription(s!)}
      />
    </Body>
  );
};

const Body = styled.div``;
